/**
 * Canonical ordering for product size attribute values.
 *
 * Size keys are derived in PHP from the attribute value label — lowercased,
 * with spaces turned into underscores — so slashes survive:
 * "King/Cal King" becomes "king/cal_king". See
 * _pur_commercetools_build_product_array() in pur_commercetools.module.
 *
 * Combined sizes are ranked by their smallest component, so any new combination
 * Commercetools introduces lands in the right place without an entry here.
 */

/**
 * Every attribute name whose values are sizes that should be ordered. These are
 * the names Drupal gives them, which for some product types differ from the
 * Commercetools "size" — see the mapping in ProductsUpdateQueueWorker.
 */
const ORDERED_SIZE_ATTRIBUTES = [
  'size',
  'bedding-size',
  'pillow-size',
  'apparel-size',
  'pet-bed-size',
];

/**
 * Every size we sell, smallest to largest. Pillow, bedding and apparel sizes
 * share one list because their value names never collide.
 */
const SIZES_ORDER = [
  'travel',
  'travel_size',
  'standard',
  'kids_twin',
  'twin',
  'twin_xl',
  'full',
  'full_xl',
  'queen',
  'king',
  'cal_king',
  'split_king',
  'small',
  'medium',
  'large',
  'xl',
] as const;

/**
 * Spellings that reach us from Commercetools or from legacy deep links, where
 * the "/" separator of a combined size was already lost.
 */
const SIZE_KEY_ALIASES: Record<string, string> = {
  twinxl: 'twin_xl',
  fullxl: 'full_xl',
  calking: 'cal_king',
  sp_king: 'split_king',
  splitking: 'split_king',
  extra_large: 'xl',
  twintwinxl: 'twin',
  king_cal_king: 'king',
  full_full_xl_queen: 'full',
  queen_full_full_xl: 'full',
  full_queen_king_cal: 'full',
  standard_6_inch: 'standard',
};

/**
 * Decoration around the size in some Commercetools values, such as the
 * "OOP Twin XL White" family of the mattress-type, where the size sits in the
 * middle of the value.
 */
const stripSizeDecorations = (sizeKey: string): string =>
  sizeKey.replace(/^oop_/, '').replace(/_white$/, '');

/** Ranked after every known size, keeping their incoming order between them. */
const UNKNOWN_SIZE_PRIORITY = Number.MAX_SAFE_INTEGER;

const SIZES_PRIORITY: Record<string, number> = Object.fromEntries(
  SIZES_ORDER.map((size, index) => [size, index]),
);

/**
 * Turn a raw attribute value key into one of the SIZES_ORDER spellings.
 * Hyphens and spaces become underscores; the "/" of a combined size is kept.
 */
const normalizeSizeKey = (sizeKey: string): string =>
  (sizeKey ?? '')
    .toLowerCase()
    .trim()
    .replace(/[\s-]+/g, '_')
    .replace(/[^a-z0-9_/]/g, '')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');

const resolveAlias = (sizeKey: string): string =>
  SIZE_KEY_ALIASES[sizeKey] ?? sizeKey;

/**
 * Sort weight of a size key. Combined sizes ("king/cal_king") take the weight of
 * their smallest component plus a half step, so they sit right after that size
 * and before the next one.
 */
const getSizePriority = (sizeKey: string): number => {
  const bare = normalizeSizeKey(sizeKey);
  const stripped = stripSizeDecorations(bare);
  const normalized = resolveAlias(stripped);
  // A decorated value sorts just after the plain size it decorates, so its
  // position never depends on the order it arrived in.
  const decoration = stripped === bare ? 0 : 0.25;

  if (normalized in SIZES_PRIORITY) {
    return SIZES_PRIORITY[normalized] + decoration;
  }

  const parts = normalized
    .split('/')
    .map((part) => SIZES_PRIORITY[resolveAlias(part)])
    .filter((priority) => priority !== undefined);

  if (!parts.length) {
    return UNKNOWN_SIZE_PRIORITY;
  }

  return Math.min(...parts) + 0.5 + decoration;
};

/**
 * Every size we sell that a *different* product type can equivalently be
 * recommended in, keyed by the source item's own normalized size key. For
 * example a Standard pillow or pillowcase should recommend Queen sheets, and
 * a King one should recommend King/Cal King sheets -- but "standard" and
 * "king" never appear as bedding-size values themselves, so a plain size-key
 * match between a pillow and a sheet set always misses. Add an entry here
 * whenever a new cross-product recommendation needs the same translation.
 */
const RELATED_PRODUCT_SIZE_EQUIVALENCIES: Record<string, string[]> = {
  standard: ['queen'],
  king: ['king/cal_king'],
};

/**
 * The normalized size keys a related product of a possibly different type
 * (e.g. a sheet set recommended alongside a pillow) should be considered a
 * match for, given the source item's own (unnormalized) size key. Always
 * includes the source's own normalized key first, so an exact same-type
 * match is still preferred over a cross-product equivalency.
 */
const getEquivalentSizeKeys = (sourceSizeKey: string): string[] => {
  const normalized = normalizeSizeKey(sourceSizeKey);
  return [normalized, ...(RELATED_PRODUCT_SIZE_EQUIVALENCIES[normalized] ?? [])];
};

/**
 * Sort size options smallest to largest. Returns a new array — callers usually
 * receive theirs as a prop — and keeps the incoming order of any size we do not
 * recognise instead of bunching them together.
 */
const sortSizeOptions = <T>(options: T[], getKey: (option: T) => string): T[] =>
  options
    .map((option, index) => ({ option, index }))
    .sort(
      (a, b) =>
        getSizePriority(getKey(a.option)) - getSizePriority(getKey(b.option)) ||
        a.index - b.index,
    )
    .map(({ option }) => option);

export {
  ORDERED_SIZE_ATTRIBUTES,
  RELATED_PRODUCT_SIZE_EQUIVALENCIES,
  getEquivalentSizeKeys,
  SIZES_ORDER,
  SIZES_PRIORITY,
  SIZE_KEY_ALIASES,
  UNKNOWN_SIZE_PRIORITY,
  normalizeSizeKey,
  getSizePriority,
  sortSizeOptions,
};
